// vCard 3.0 export (RFC 2426). Deliberately 3.0, not 4.0: 3.0 is the universally
// supported version across iPhone (Contacts/iOS), Android + Google Contacts, and
// Outlook. 4.0 support is inconsistent on mobile, so we stay on 3.0.
//
// Cross-platform mapping strategy (the hard part — importers disagree a lot):
//   - Standard PIM fields (name/org/title/email/phone/url/adr/bday) use the
//     canonical 3.0 properties — these Just Work everywhere.
//   - SOCIAL profiles: the only thing BOTH iOS and Android reliably surface is a
//     labeled URL. We emit a grouped `itemN.URL` + `itemN.X-ABLabel:<Platform>`
//     (iOS shows the label; Android/Google keep a clickable URL) AND, as an iOS
//     bonus, `X-SOCIALPROFILE;TYPE=<svc>`. The handle is turned into a canonical
//     profile URL via the catalog url_template when it isn't already a URL.
//   - MESSAGING apps: `IMPP;X-SERVICE-TYPE=<svc>` (read by iOS) PLUS a labeled
//     URL group when a deep-link exists, so Android still shows something useful.
//   - LOYALTY / custom / anything without a URL: folded into a readable NOTE
//     block ("Marriott Bonvoy: 12345") — the one field every importer displays,
//     so no data is silently dropped like it was before.
import type { Contact, ContactField, FieldType } from '../api/types.js';

// ---- Field-type → vCard mapping table -------------------------------------
// Self-contained so export works without a live catalog fetch. Mirrors the
// compiled-in Go catalog (internal/fieldcatalog) for the well-known keys. A
// passed-in catalog (from GET /field-types) augments/overrides this at runtime.

type VKind = 'name_given' | 'name_family' | 'name_middle' | 'name_full'
  | 'org' | 'title' | 'email' | 'phone' | 'url' | 'adr' | 'bday'
  | 'social' | 'messaging' | 'note';

interface VMap {
  kind: VKind;
  // Human label shown to the user (X-ABLabel / NOTE prefix).
  label: string;
  // For social/messaging: the X-SERVICE-TYPE / X-SOCIALPROFILE service token.
  service?: string;
  // For social/messaging: URL template with <x> placeholder to canonicalize a
  // bare handle into a profile/deep link (matches the Go catalog).
  urlTemplate?: string;
}

const MAP: Record<string, VMap> = {
  // Name
  given_name: { kind: 'name_given', label: 'Given name' },
  family_name: { kind: 'name_family', label: 'Family name' },
  middle_name: { kind: 'name_middle', label: 'Middle name' },
  full_name: { kind: 'name_full', label: 'Full name' },
  display_name: { kind: 'name_full', label: 'Display name' },
  // Contact / org
  email: { kind: 'email', label: 'Email' },
  phone: { kind: 'phone', label: 'Phone' },
  url: { kind: 'url', label: 'Website' },
  company: { kind: 'org', label: 'Company' },
  job_title: { kind: 'title', label: 'Job title' },
  postal_address: { kind: 'adr', label: 'Address' },
  date_of_birth: { kind: 'bday', label: 'Birthday' },
  // Messaging (service token + optional deep-link)
  whatsapp: { kind: 'messaging', label: 'WhatsApp', service: 'WhatsApp', urlTemplate: 'https://wa.me/<x>' },
  telegram: { kind: 'messaging', label: 'Telegram', service: 'Telegram', urlTemplate: 'https://t.me/<x>' },
  signal: { kind: 'messaging', label: 'Signal', service: 'Signal', urlTemplate: 'https://signal.me/#p/<x>' },
  wechat: { kind: 'messaging', label: 'WeChat', service: 'WeChat' },
  imessage: { kind: 'messaging', label: 'iMessage', service: 'iMessage' },
  messenger: { kind: 'messaging', label: 'Messenger', service: 'Messenger', urlTemplate: 'https://m.me/<x>' },
  line: { kind: 'messaging', label: 'LINE', service: 'LINE' },
  viber: { kind: 'messaging', label: 'Viber', service: 'Viber' },
  discord: { kind: 'messaging', label: 'Discord', service: 'Discord' },
  slack: { kind: 'messaging', label: 'Slack', service: 'Slack' },
  matrix: { kind: 'messaging', label: 'Matrix', service: 'Matrix' },
  skype: { kind: 'messaging', label: 'Skype', service: 'Skype' },
  kakaotalk: { kind: 'messaging', label: 'KakaoTalk', service: 'KakaoTalk' },
  kik: { kind: 'messaging', label: 'Kik', service: 'Kik' },
  // Social (labeled canonical URL)
  linkedin: { kind: 'social', label: 'LinkedIn', service: 'linkedin', urlTemplate: 'https://linkedin.com/in/<x>' },
  instagram: { kind: 'social', label: 'Instagram', service: 'instagram', urlTemplate: 'https://instagram.com/<x>' },
  facebook: { kind: 'social', label: 'Facebook', service: 'facebook', urlTemplate: 'https://facebook.com/<x>' },
  x_twitter: { kind: 'social', label: 'X', service: 'twitter', urlTemplate: 'https://x.com/<x>' },
  tiktok: { kind: 'social', label: 'TikTok', service: 'tiktok', urlTemplate: 'https://tiktok.com/@<x>' },
  youtube: { kind: 'social', label: 'YouTube', service: 'youtube', urlTemplate: 'https://youtube.com/@<x>' },
  github: { kind: 'social', label: 'GitHub', service: 'github', urlTemplate: 'https://github.com/<x>' },
  gitlab: { kind: 'social', label: 'GitLab', service: 'gitlab', urlTemplate: 'https://gitlab.com/<x>' },
  mastodon: { kind: 'social', label: 'Mastodon', service: 'mastodon' },
  bluesky: { kind: 'social', label: 'Bluesky', service: 'bluesky', urlTemplate: 'https://bsky.app/profile/<x>' },
  threads: { kind: 'social', label: 'Threads', service: 'threads', urlTemplate: 'https://threads.net/@<x>' },
  snapchat: { kind: 'social', label: 'Snapchat', service: 'snapchat' },
  pinterest: { kind: 'social', label: 'Pinterest', service: 'pinterest', urlTemplate: 'https://pinterest.com/<x>' },
  reddit: { kind: 'social', label: 'Reddit', service: 'reddit', urlTemplate: 'https://reddit.com/user/<x>' },
  twitch: { kind: 'social', label: 'Twitch', service: 'twitch', urlTemplate: 'https://twitch.tv/<x>' },
  behance: { kind: 'social', label: 'Behance', service: 'behance', urlTemplate: 'https://behance.net/<x>' },
  dribbble: { kind: 'social', label: 'Dribbble', service: 'dribbble', urlTemplate: 'https://dribbble.com/<x>' },
  medium: { kind: 'social', label: 'Medium', service: 'medium', urlTemplate: 'https://medium.com/@<x>' },
  substack: { kind: 'social', label: 'Substack', service: 'substack' },
  calendly: { kind: 'social', label: 'Calendly', service: 'calendly', urlTemplate: 'https://calendly.com/<x>' },
  spotify: { kind: 'social', label: 'Spotify', service: 'spotify', urlTemplate: 'https://open.spotify.com/user/<x>' },
  rednote: { kind: 'social', label: 'RedNote', service: 'rednote' },
  amazon: { kind: 'social', label: 'Amazon', service: 'amazon', urlTemplate: 'https://www.amazon.com/shop/<x>' },
};

// vmapFor resolves a field's vCard mapping. Precedence: static MAP, then the
// runtime catalog (url_template/label), else a NOTE line so nothing is dropped.
function vmapFor(type: string, catalog?: FieldType[]): VMap {
  const m = MAP[type];
  if (m) return m;
  if (catalog) {
    const hit = catalog.find((t) => t.key === type);
    if (hit) {
      const label = hit.label || prettyKey(type);
      if (hit.category === 'social') return { kind: 'social', label, service: type, urlTemplate: hit.url_template };
      if (hit.category === 'messaging') return { kind: 'messaging', label, service: label, urlTemplate: hit.url_template };
      return { kind: 'note', label };
    }
  }
  return { kind: 'note', label: prettyKey(type) };
}

// prettyKey turns a snake/custom key into a human label ("world_of_hyatt" ->
// "World of hyatt"; "custom_gym_id" -> "Gym id").
function prettyKey(key: string): string {
  const k = key.replace(/^custom_/, '').replace(/_/g, ' ').trim();
  return k ? k.charAt(0).toUpperCase() + k.slice(1) : key;
}

// canonHandleURL turns a bare handle into a profile/deep-link URL using a
// template; if the value already looks like a URL, it's returned as-is.
function canonHandleURL(value: string, urlTemplate?: string): string | null {
  const v = value.trim();
  if (!v) return null;
  if (/^https?:\/\//i.test(v)) return v;
  if (!urlTemplate) return null;
  let handle = v.replace(/^[@$]/, '');
  // wa.me / viber want digits only.
  if (/wa\.me|signal\.me/.test(urlTemplate)) handle = handle.replace(/[^\d]/g, '') || handle;
  return urlTemplate.replace('<x>', encodeURIComponent(handle).replace(/%40/g, '@'));
}

// toBday converts stored date forms (YYYY/MM/DD or YYYY-MM-DD) to vCard BDAY
// ISO (YYYY-MM-DD). Returns null if it isn't a full date (e.g. MM/DD).
function toBday(value: string): string | null {
  const n = value.trim().replace(/[./]/g, '-');
  return /^\d{4}-\d{2}-\d{2}$/.test(n) ? n : null;
}

// ---- Core generator --------------------------------------------------------

export interface VCardInput {
  fn: string;                 // pre-resolved full/display name (FN)
  first?: string; last?: string;
  fields: { type: string; value: string; label?: string; annotation?: string }[];
  company?: string;
  note?: string;
  labels?: string[];
}

// buildVCard is the single, shared 3.0 generator used by every export path.
export function buildVCard(input: VCardInput, catalog?: FieldType[]): string {
  const lines: string[] = ['BEGIN:VCARD', 'VERSION:3.0'];
  const fn = input.fn || 'Unknown';
  lines.push(`FN:${esc(fn)}`);
  const { first, last } = resolveN(input, fn);
  lines.push(`N:${esc(last)};${esc(first)};;;`);

  // Grouped-item counter for labeled URLs (itemN.URL / itemN.X-ABLabel).
  let item = 0;
  const noteExtra: string[] = [];
  let org = (input.company || '').trim();
  let title = '';
  const seen = new Set<string>();

  for (const f of input.fields || []) {
    const type = (f.type || '').trim();
    const value = (f.value || '').trim();
    if (!type || !value) continue;
    // De-dupe identical type+value pairs.
    const dk = `${type}=${value}`;
    if (seen.has(dk)) continue;
    seen.add(dk);

    const mBase = vmapFor(type, catalog);
    // Copy so we never mutate the shared static MAP entry (vmapFor may return it).
    const m = { ...mBase };
    // Custom fields carry their display NAME in `label` and an optional qualifier
    // in `annotation`; catalog fields carry the qualifier in `label`. Build a
    // human label that preserves the owner's naming/annotation so nothing is lost
    // in export (e.g. "Gym ID (work)").
    const isCustom = type.startsWith('custom_');
    const annot = (f.annotation || '').trim();
    if (isCustom && (f.label || '').trim()) m.label = (f.label as string).trim();
    if (annot) m.label = `${m.label} (${annot})`;
    switch (m.kind) {
      case 'name_given': case 'name_family': case 'name_middle': case 'name_full':
        break; // already folded into FN/N
      case 'org': if (!org) org = value; break;
      case 'title': title = value; break;
      case 'email': lines.push(`EMAIL;TYPE=INTERNET:${esc(value)}`); break;
      case 'phone': lines.push(`TEL;TYPE=CELL:${esc(value)}`); break;
      case 'url': lines.push(`URL:${esc(value)}`); break;
      case 'adr': lines.push(`ADR;TYPE=WORK:;;${esc(value)};;;;`); break;
      case 'bday': {
        const b = toBday(value);
        if (b) lines.push(`BDAY:${b}`);
        else noteExtra.push(`${m.label}: ${value}`);
        break;
      }
      case 'social': {
        const url = canonHandleURL(value, m.urlTemplate);
        if (url) {
          item++;
          lines.push(`item${item}.URL:${esc(url)}`);
          lines.push(`item${item}.X-ABLabel:${esc(m.label)}`);
          if (m.service) lines.push(`X-SOCIALPROFILE;TYPE=${esc(m.service)}:${esc(url)}`);
        } else {
          // No URL derivable (e.g. Snapchat/RedNote handle) — labeled note.
          noteExtra.push(`${m.label}: ${value}`);
        }
        break;
      }
      case 'messaging': {
        if (m.service) lines.push(`IMPP;X-SERVICE-TYPE=${esc(m.service)}:${esc(value)}`);
        const url = canonHandleURL(value, m.urlTemplate);
        if (url) {
          item++;
          lines.push(`item${item}.URL:${esc(url)}`);
          lines.push(`item${item}.X-ABLabel:${esc(m.label)}`);
        } else if (!m.service) {
          noteExtra.push(`${m.label}: ${value}`);
        }
        break;
      }
      case 'note':
      default:
        // Loyalty / custom / unmapped: readable, always-visible NOTE line.
        noteExtra.push(`${m.label}: ${value}`);
        break;
    }
  }

  if (org) lines.push(`ORG:${esc(org)}`);
  if (title) lines.push(`TITLE:${esc(title)}`);

  // Merge the owner-local note (if any) with the collected loyalty/custom lines.
  const noteParts: string[] = [];
  if (input.note && input.note.trim()) noteParts.push(input.note.trim());
  if (noteExtra.length) noteParts.push(noteExtra.join('\n'));
  if (noteParts.length) lines.push(`NOTE:${esc(noteParts.join('\n\n'))}`);

  if (input.labels?.length) lines.push(`CATEGORIES:${input.labels.map(esc).join(',')}`);
  lines.push('END:VCARD');
  return lines.join('\r\n');
}

// resolveN splits a full name into N (family;given) parts, preferring explicit
// given/family fields when present in the input.
function resolveN(input: VCardInput, fn: string): { first: string; last: string } {
  if (input.first || input.last) return { first: input.first || '', last: input.last || '' };
  const parts = fn.split(/\s+/).filter(Boolean);
  const last = parts.length > 1 ? parts.pop()! : '';
  return { first: parts.join(' '), last };
}

// ---- Contact adapter (My Contacts export) ---------------------------------

export function contactToVCard(c: Contact, catalog?: FieldType[]): string {
  // Only SYNCING fields belong on the exported card; stale fields are excluded.
  const fields = (c.fields || []).filter((cf: ContactField) => cf.syncing && cf.type && cf.value)
    .map((cf) => ({ type: cf.type, value: cf.value, label: cf.label, annotation: cf.annotation }));
  const given = fields.find((f) => f.type === 'given_name')?.value || '';
  const family = fields.find((f) => f.type === 'family_name')?.value || '';
  const realName = [given, family].filter(Boolean).join(' ').trim();
  const fn = realName || c.owner_handle || c.display_name || 'Unknown';
  return buildVCard({
    fn,
    first: given || undefined,
    last: family || undefined,
    fields,
    company: c.company,
    note: c.note,
    labels: c.labels,
  }, catalog);
}

function esc(s: string): string {
  return s.replace(/([,;\\])/g, '\\$1').replace(/\n/g, '\\n');
}

export function downloadVCard(filename: string, content: string): void {
  const blob = new Blob([content], { type: 'text/vcard;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; document.body.append(a); a.click();
  a.remove(); setTimeout(() => URL.revokeObjectURL(url), 1000);
}
